/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import {get} from '../../src/environment/environment';
import {error} from '../../src/logger/logger';
import {MapPosition} from '../types/general';
import {getQueryString} from '../utils/ObjectUtil';
import {
  client,
  config,
  createCSV,
  createFleetRequestUrl,
  getHereAPIKey,
  parseSuggestions
} from './HereUtils';

export interface SuggestMapSearchParam {
  text: string;
  cancelToken?: any;
  maxResults?: number;
  showMatchLevel?: boolean;
  center?: MapPosition;
};

/**
 * Parameters being used for HERE api
 *
 */
export const ALIGNED_ROUTES_CONFIG = {
  ALIGNED_ROUTES_MODE         : 'fastest;car;traffic:disabled',
  ALIGNED_ROUTES_CSV_HEADERS  : 'LONGITUDE, LATITUDE, HEADING',
  ALIGNED_ROUTES_MAX_DISTANCE : 300 // units in meters
};

/**
*
* getAlignedRoutes takes array of coordinate nodes returns
* a response from HERE api*
*
*```typescript
*
* import {getAlignedRoutes} from '@ion/core/src/Here';
*
* const response = getAlignedRoutes(assets: CVSAssetType[])
* ...
*```
*/
export const getAlignedRoutes = async(assets): Promise<any> => {
  const {hereFleetServiceURL, hereApiKey} = config || {};
  if (hereFleetServiceURL && hereApiKey) {
    if (assets.length >= 1) {
      const {
        ALIGNED_ROUTES_MODE,
        ALIGNED_ROUTES_CSV_HEADERS
      } = ALIGNED_ROUTES_CONFIG;

      const url      = createFleetRequestUrl(hereFleetServiceURL, ALIGNED_ROUTES_MODE, hereApiKey);
      const body     = createCSV(assets, ALIGNED_ROUTES_CSV_HEADERS);
      const response = await client.post(url, body);
      return {assetId: assets[0].assetId, response};
    }
  } else {
    error('invalid HERE env. Please check environment.json');
  }
};

export const massRouteMatching = async(assetsMap) => {
  const featureFlagsEnabled = get('featureFlagsEnabled');
  if (featureFlagsEnabled) {
    const promises = Object.values(assetsMap)
      .map(asset => new Promise((resolve, reject) => resolve(getAlignedRoutes(asset))));
    const response = (await Promise.allSettled(promises)).filter(({status}) => status === 'fulfilled');
    return response;
  }
  return assetsMap;
};

/**
 * The function is used to get the list of suggest places from the HERE service.
 *
 * ```typescript
 *
 *  import {suggestMapSearch} from '@ion/core/src/Here';
 *
 *  const suggestions = suggestMapSearch('Mambadi, Dharaphuram', 5);
 *
 * ```
 *
 * @param text       The text used to search for place suggestions
 * @param maxResults Maximum number of results to return
 * @returns suggest places from the HERE suggestion API
 */
export const suggestMapSearch = async({
  text,
  center,
  cancelToken,
  maxResults = 4,
  showMatchLevel = false
}: SuggestMapSearchParam): Promise<any> => {
  const params = {
    q      : text,
    lang   : 'en',
    apikey : getHereAPIKey() || get('hereApiKey'),
    limit  : maxResults,
    ...(center && {at: center.join(',')})
  };
  const response = await client({
    url    : `https://autosuggest.search.hereapi.com/v1/autosuggest?${getQueryString(params)}`,
    method : 'get',
    ...(cancelToken && {cancelToken})
  });
  return parseSuggestions(response, showMatchLevel);
};

/**
 * The function is used to retrieve the more details of the location data
 * from the HERE service.
 *
 * ```typescript
 *
 *  import {getMapAddressCandidates} from '@ion/core/src/Here';
 *
 *  const detail = getMapAddressCandidates('adfrhj8989_sdhh789');
 *
 * ```
 * @param locationId The unique identifier for the location
 * @returns HERE place details by locationId
 */
export const getMapAddressCandidates = async(locationId: string, useLatestVersionAPI = false): Promise<any> => {
  const url = useLatestVersionAPI ? 'https://lookup.search.hereapi.com/v1/lookup' : 'https://geocoder.ls.hereapi.com/6.2/geocode.json';
  const queryParams = useLatestVersionAPI ? {
    apiKey : getHereAPIKey(),
    id     : locationId,
    lang   : 'en'
  } : {
    xnlp   : 'CL_JSMv3.1.35.3',
    apiKey : getHereAPIKey(),
    locationId
  };
  const response = await client({
    url    : `${url}?${getQueryString(queryParams)}`,
    method : 'get'
  });
  return response;
};
